/**
 * StageIndicator Component
 *
 * Displays the current stage of a governance discussion with visual styling
 * and optional timeline view.
 *
 * Story FOS-4.2.3: Leaf Components Batch 1 (Stage/Quality)
 *
 * Features:
 * - Brainstorm (yellow), Refining (blue), Ready (green) stages
 * - Stage transition timestamp display
 * - Optional timeline view showing progression
 * - Multiple size variants
 *
 * @module components/governance/StageIndicator
 * @see AC-4.1.5.2 - Discussion stage clearly visible with appropriate visual treatment
 */

import { Lightbulb, RefreshCw, CheckCircle } from 'lucide-react';
import type { DiscussionStage } from '../../stores/discussionStore';

/**
 * Props for StageIndicator component
 */
export interface StageIndicatorProps {
  /** Current discussion stage */
  stage: DiscussionStage;
  /** Timestamp when stage last changed (optional) */
  stageChangedAt?: number;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Whether to show timeline progression */
  showTimeline?: boolean;
  /** Optional class name */
  className?: string;
}

/**
 * Stage configuration with colors and icons
 */
const STAGE_CONFIG = {
  Brainstorm: {
    label: 'Brainstorm',
    icon: Lightbulb,
    bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
    textColor: 'text-yellow-800 dark:text-yellow-200',
    borderColor: 'border-yellow-300 dark:border-yellow-700',
    dotColor: 'bg-yellow-500',
  },
  Refining: {
    label: 'Refining',
    icon: RefreshCw,
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    textColor: 'text-blue-800 dark:text-blue-200',
    borderColor: 'border-blue-300 dark:border-blue-700',
    dotColor: 'bg-blue-500',
  },
  Ready: {
    label: 'Ready',
    icon: CheckCircle,
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    textColor: 'text-green-800 dark:text-green-200',
    borderColor: 'border-green-300 dark:border-green-700',
    dotColor: 'bg-green-500',
  },
} as const;

/**
 * Stage order for timeline progression
 */
const STAGE_ORDER: DiscussionStage[] = ['Brainstorm', 'Refining', 'Ready'];

/**
 * Size configuration
 */
const SIZE_CONFIG = {
  sm: {
    badge: 'px-2 py-0.5 text-xs',
    icon: 'h-3 w-3',
    dot: 'w-2 h-2',
    line: 'h-0.5',
  },
  md: {
    badge: 'px-3 py-1 text-sm',
    icon: 'h-4 w-4',
    dot: 'w-3 h-3',
    line: 'h-0.5',
  },
  lg: {
    badge: 'px-4 py-1.5 text-base',
    icon: 'h-5 w-5',
    dot: 'w-4 h-4',
    line: 'h-1',
  },
};

/**
 * Format a timestamp relative to now
 */
function formatRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diffMs = now - timestamp;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) {
    return 'Just changed';
  } else if (diffMins < 60) {
    return `${diffMins}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else {
    return `${diffDays}d ago`;
  }
}

/**
 * Get stage index for timeline comparison
 */
function getStageIndex(stage: DiscussionStage): number {
  return STAGE_ORDER.indexOf(stage);
}

/**
 * Timeline Step component
 */
function TimelineStep({
  stage,
  currentStage,
  size,
  isLast,
}: {
  stage: DiscussionStage;
  currentStage: DiscussionStage;
  size: 'sm' | 'md' | 'lg';
  isLast: boolean;
}) {
  const config = STAGE_CONFIG[stage];
  const sizeConfig = SIZE_CONFIG[size];
  const currentIndex = getStageIndex(currentStage);
  const stepIndex = getStageIndex(stage);
  const isComplete = stepIndex < currentIndex;
  const isCurrent = stage === currentStage;

  const Icon = config.icon;

  return (
    <div
      className="flex items-center"
      data-testid="timeline-step"
      data-stage={stage}
      data-complete={isComplete.toString()}
      data-current={isCurrent.toString()}
    >
      {/* Dot/Icon */}
      <div
        className={`flex items-center justify-center rounded-full ${sizeConfig.dot} ${
          isComplete || isCurrent ? config.dotColor : 'bg-muted'
        } ${isCurrent ? 'ring-2 ring-offset-2 ring-offset-background' : ''}`}
        style={isCurrent ? { '--tw-ring-color': config.dotColor.replace('bg-', '') } as React.CSSProperties : undefined}
      >
        {(isComplete || isCurrent) && (
          <Icon className={`${size === 'sm' ? 'h-1.5 w-1.5' : 'h-2 w-2'} text-white`} />
        )}
      </div>

      {/* Label */}
      <span
        className={`ml-2 ${size === 'sm' ? 'text-xs' : 'text-sm'} ${
          isComplete || isCurrent ? 'font-medium text-foreground' : 'text-muted-foreground'
        }`}
      >
        {config.label}
      </span>

      {/* Connector line */}
      {!isLast && (
        <div
          className={`flex-1 mx-2 ${sizeConfig.line} ${
            isComplete ? config.dotColor : 'bg-muted'
          }`}
        />
      )}
    </div>
  );
}

/**
 * StageIndicator - Displays discussion stage with visual styling
 *
 * @example
 * ```tsx
 * // Simple badge
 * <StageIndicator stage="Brainstorm" />
 *
 * // With timestamp
 * <StageIndicator stage="Refining" stageChangedAt={Date.now() - 3600000} />
 *
 * // With timeline
 * <StageIndicator stage="Ready" showTimeline={true} size="lg" />
 * ```
 */
export function StageIndicator({
  stage,
  stageChangedAt,
  size = 'md',
  showTimeline = false,
  className = '',
}: StageIndicatorProps) {
  const config = STAGE_CONFIG[stage];
  const sizeConfig = SIZE_CONFIG[size];
  const Icon = config.icon;

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      {/* Stage badge */}
      <div className="flex items-center gap-2">
        <span
          className={`inline-flex items-center gap-1.5 rounded-full border ${config.bgColor} ${config.textColor} ${config.borderColor} ${sizeConfig.badge} font-medium transition-colors`}
          data-testid="stage-badge"
          data-stage={stage}
          data-size={size}
          aria-label={`Discussion stage: ${stage}`}
        >
          <Icon className={sizeConfig.icon} />
          {config.label}
        </span>

        {/* Timestamp */}
        {stageChangedAt && (
          <span
            className="text-xs text-muted-foreground"
            data-testid="stage-timestamp"
          >
            {formatRelativeTime(stageChangedAt)}
          </span>
        )}
      </div>

      {/* Timeline view */}
      {showTimeline && (
        <div
          className="flex items-center w-full"
          data-testid="stage-timeline"
        >
          {STAGE_ORDER.map((s, index) => (
            <TimelineStep
              key={s}
              stage={s}
              currentStage={stage}
              size={size}
              isLast={index === STAGE_ORDER.length - 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default StageIndicator;
